import {
  createMaterial,
  deleteMaterial,
  editMaterial,
  listOfMaterial,
  viewMaterial,
} from "Api/Material";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import BaseModal from "Components/Base/BaseModal";
import DeleteModal from "Components/Base/DeleteModal";
import Pagination from "Components/Common/Pagination";
import withRouter from "Components/Common/withRouter";
import {
  checkStatusCodeSuccess,
  Loading,
  notFound,
} from "Components/constants/Common";
import { renderSkeletons } from "Components/constants/layout";
import {
  addMaterialLabel,
  editMaterialLabel,
  materialLabel,
  materialName,
} from "Components/constants/Material";
import {
  cancelLabel,
  DeleteLabel,
  editLabel,
  NewLabel,
  submitLabel,
  updateLabel,
} from "Components/constants/Role";
import {
  InputPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { RiSearchLine } from "react-icons/ri";
import { toast } from "react-toastify";
import { Spinner } from "reactstrap";
import * as Yup from "yup";

const Material = (props: any) => {
  document.title = materialLabel;

  const [loader, setLoader] = useState<boolean>(false);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [materials, setMaterials] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [selectedMaterialId, setSelectedMaterialId] = useState<number | null>(
    null
  );
  const [btnLoader, setBtnLoader] = useState<boolean>(false);
  const [modalLoader, setModalLoader] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalRecords, setTotalRecords] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [onValueSearch, setOnValueSearch] = useState<string>("");
  const [materialData, setMaterialData] = useState<string>("");

  const materialModal = () => {
    setIsModalOpen(true);
    setMaterialData("");
  };

  const closeModal = () => {
    setIsModalOpen(false);
    formik.resetForm();
    setSelectedMaterialId(null);
    setEditMode(false);
    setMaterialData("");
  };

  const fetchMaterials = async () => {
    const payload = {
      pageSize,
      page: currentPage,
      search: onValueSearch,
    };
    setLoader(true);
    listOfMaterial(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setMaterials(res?.data?.materials || []);
          setTotalRecords(res?.data?.totalItems || 0);
          setTotalPages(res?.data?.totalPages);
          setCurrentPage(res?.data?.currentPage);
          toast.success(res?.message);
        } else {
          setMaterials([]);
        }
      })
      .catch(() => {
        setMaterials([]);
      })
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    fetchMaterials();
  }, [currentPage, pageSize, onValueSearch]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: materialData ? materialData : null,
    },
    validationSchema: Yup.object({
      name: Yup.string().required(validationMessages.required(materialName)),
    }),
    onSubmit: async (values) => {
      setSubmitLoader(true);
      const payload = {
        name: values.name,
      };

      if (selectedMaterialId) {
        editMaterial(selectedMaterialId, payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              toast.success(res?.message);
              fetchMaterials();
              closeModal();
            } else {
              toast.error(res?.message);
            }
          })
          .catch((error) => {
            toast.error(error?.response?.data?.message || error?.message);
          })
          .finally(() => setSubmitLoader(false));
      } else {
        createMaterial(payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              toast.success(res?.message);
              fetchMaterials();
              closeModal();
            } else {
              toast.error(res?.message);
            }
          })
          .catch((error) => {
            toast.error(error?.response?.data?.message || error?.message);
            setSubmitLoader(false);
          })
          .finally(() => setSubmitLoader(false));
      }
    },
  });

  const openDeleteModal = (id: number) => {
    setSelectedMaterialId(id);
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
    setSelectedMaterialId(null);
  };

  const handleDeleteMaterial = () => {
    if (selectedMaterialId !== null) {
      setBtnLoader(true);
      deleteMaterial(selectedMaterialId)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            fetchMaterials();
          } else {
            toast.error(res?.message);
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message || error?.message);
        })
        .finally(() => {
          setBtnLoader(false);
          closeDeleteModal();
        });
    }
  };

  const viewMaterialData = (id: number) => {
    materialModal();
    setModalLoader(true);
    viewMaterial(id)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setMaterialData(res?.data?.name);
          setSelectedMaterialId(id);
        } else {
          toast.error(res?.message);
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || error?.message);
      })
      .finally(() => setModalLoader(false));
  };

  const openEditModal = async (id: number) => {
    setEditMode(true);
    viewMaterialData(id);
    setModalLoader(true);
  };

  return (
    <div className="page-content">
      <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
        <span className="fs-4 d-flex gap-4">{materialLabel} </span>
        <div className="d-flex align-items-center gap-2">
          <BaseButton
            className="btn btn-success"
            onClick={materialModal}
            children={
              <>
                <i className="mdi mdi-plus" /> {NewLabel}
              </>
            }
          />
        </div>
      </div>

      <Pagination
        pageSizeValue={pageSize}
        totalPages={totalPages}
        totalRecords={totalRecords}
        setOnValueSearchBar={setOnValueSearch}
        data={materials}
        setPageSizeValue={setPageSize}
        setCurrentPageValue={setCurrentPage}
        children={
          <>
            {" "}
            {loader ? (
              <div className="d-flex row mt-3">{renderSkeletons(pageSize)}</div>
            ) : (
              <div className="d-flex row mt-3">
                {materials?.length > 0 ? (
                  materials?.map((material, index) => (
                    <div key={index} className="col-lg-3 col-md-4 col-12">
                      <div className="card card-animate ribbon-box overflow-hidden">
                        <div className="card-body py-4 text-center">
                          <div className="ribbon ribbon-shape shadow-none ribbon-info">
                            {materialLabel}
                          </div>
                          <div
                            className={
                              material.name.length > 12
                                ? `d-flex align-items-end justify-content-between mt-4 tooltip-container`
                                : `d-flex align-items-end justify-content-between mt-4`
                            }
                          >
                            <span className="material-name">
                              {material.name}
                            </span>
                            <div className="d-flex align-items-end text-truncate">
                              <h4 className="fs-22 fw-semibold ff-secondary mb-0 pb-1 text-wrap ">
                                {material.name.length > 12 ? (
                                  <span>
                                    {material.name.substring(0, 12)}...
                                  </span>
                                ) : (
                                  material.name
                                )}
                              </h4>
                            </div>
                          </div>
                          <div className="text-center d-flex justify-content-end mt-2 gap-2">
                            <BaseButton
                              className="btn btn-light py-0 px-2"
                              onClick={() => openEditModal(material.id)}
                            >
                              <span className="tooltip-container">
                                <i className="mdi mdi-pencil-outline fs-4 text-dark cursor-pointer"></i>
                                <span className="tooltip-text">
                                  {editLabel}
                                </span>
                              </span>
                            </BaseButton>
                            {/* delete future purpose */}
                            {/* <BaseButton
                              className="btn btn-light py-0 px-2"
                              onClick={() => openDeleteModal(material.id)}
                            >
                              <span className="tooltip-container">
                                <i className="mdi mdi-delete-outline fs-4 text-danger cursor-pointer"></i>
                                <span className="tooltip-text">
                                  {DeleteLabel}
                                </span>
                              </span>
                            </BaseButton> */}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="card py-5">
                    <div className="py-4 text-center">
                      <div>
                        <RiSearchLine className="fs-2" />
                      </div>
                      <div className="mt-4">
                        <h5>{notFound.dataNotFound}</h5>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        }
      />

      <BaseModal
        open={isModalOpen}
        toggle={closeModal}
        title={editMode ? editMaterialLabel : addMaterialLabel}
        submit={formik.handleSubmit}
        closeLabel={cancelLabel}
        submitLabel={editMode ? updateLabel : submitLabel}
        closeLabelColor="danger"
        submitLabelColor="success"
        loader={submitLoader}
        isSubmitDisabled={!formik.dirty}
      >
        {modalLoader ? (
          <div className="d-flex justify-content-center align-items-center">
            <Spinner>{Loading}</Spinner>
          </div>
        ) : (
          <div className="form-group">
            <BaseInput
              type="text"
              label={materialName}
              placeholder={InputPlaceHolder(materialName)}
              name="name"
              className="form-control"
              value={formik.values.name}
              onChange={formik.handleChange}
              handleBlur={formik.handleBlur}
              touched={formik.touched.name}
              error={formik.errors.name}
              required
            />
          </div>
        )}
      </BaseModal>

      <DeleteModal
        show={deleteModal}
        onCloseClick={closeDeleteModal}
        onDeleteClick={handleDeleteMaterial}
        btnLoader={btnLoader}
      />
    </div>
  );
};

export default withRouter(Material);
